
import React, { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ImageIcon, Layers, Palette, Share2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface CanvaDesignToolProps {
  onDesignSave?: (designUrl: string) => void;
  designType?: 'social' | 'cover' | 'poster' | 'video';
  className?: string;
}

declare global {
  interface Window {
    Canva?: {
      DesignButton: {
        setupButton: (options: {
          apiKey: string;
          button: {
            element: string;
            text: string;
            loggedOutText?: string;
          };
          design: {
            type: string;
            dimensions?: {
              width: number;
              height: number;
            };
          };
          onDesignOpen?: () => void;
          onDesignPublish?: (options: { exportUrl: string }) => void;
          onDesignError?: (error: any) => void;
        }) => void;
      };
    };
  }
}

// This would normally come from an environment variable
// For a real implementation, you would need to sign up for Canva Developer Program
// and get an API key: https://www.canva.com/developers/
const CANVA_API_KEY = 'YOUR_CANVA_API_KEY'; 

const designTypes = {
  social: {
    title: 'Social Media Post',
    description: 'Perfect for Instagram, Facebook, Twitter',
    type: 'social-media',
    dimensions: { width: 1080, height: 1080 }
  },
  cover: {
    title: 'Album/Single Cover',
    description: 'Standard dimensions for music platforms',
    type: 'graphic',
    dimensions: { width: 3000, height: 3000 }
  },
  poster: {
    title: 'Tour Poster',
    description: 'Promotional material for shows',
    type: 'poster',
    dimensions: { width: 18 * 72, height: 24 * 72 }
  },
  video: {
    title: 'Video Thumbnail',
    description: 'Thumbnail for YouTube and video platforms',
    type: 'youtube-thumbnail',
    dimensions: { width: 1280, height: 720 }
  }
};

const CanvaDesignTool: React.FC<CanvaDesignToolProps> = ({ 
  onDesignSave, 
  designType = 'social',
  className = ''
}) => {
  const { toast } = useToast();
  const canvaButtonRef = useRef<HTMLDivElement>(null);
  const [activeDesignType, setActiveDesignType] = React.useState<'social' | 'cover' | 'poster' | 'video'>(designType);

  useEffect(() => {
    // Load Canva button script
    const script = document.createElement('script');
    script.src = 'https://sdk.canva.com/designbutton/v2/api.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = initializeCanvaButton;

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    // Reinitialize when design type changes
    if (window.Canva) {
      initializeCanvaButton();
    }
  }, [activeDesignType]);

  const initializeCanvaButton = () => {
    if (!window.Canva || !canvaButtonRef.current) return;

    const design = designTypes[activeDesignType];

    try {
      window.Canva.DesignButton.setupButton({
        apiKey: CANVA_API_KEY,
        button: {
          element: "#canva-design-button",
          text: `Design ${design.title} with Canva`,
          loggedOutText: "Log in to Canva to design"
        },
        design: {
          type: design.type,
          dimensions: design.dimensions
        },
        onDesignOpen: () => {
          toast({
            title: "Canva editor opened",
            description: "Design away! Your creation will be saved back to this app when done."
          });
        },
        onDesignPublish: ({ exportUrl }) => {
          toast({
            title: "Design saved!",
            description: "Your design has been successfully created."
          });
          if (onDesignSave) {
            onDesignSave(exportUrl);
          }
        },
        onDesignError: (error) => {
          toast({
            title: "Error with Canva",
            description: "There was an issue with the Canva editor. Please try again.",
            variant: "destructive"
          });
          console.error("Canva error:", error);
        }
      });
    } catch (err) {
      console.error("Error setting up Canva button:", err);
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-purple-500" />
          Design with Canva
        </CardTitle>
        <CardDescription>
          Create professional graphics for your marketing campaign
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeDesignType} onValueChange={(v) => setActiveDesignType(v as any)} className="w-full">
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="social" className="text-xs">
              <div className="flex flex-col items-center gap-1">
                <ImageIcon className="h-4 w-4" />
                <span>Social</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="cover" className="text-xs">
              <div className="flex flex-col items-center gap-1">
                <Layers className="h-4 w-4" />
                <span>Cover</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="poster" className="text-xs">
              <div className="flex flex-col items-center gap-1">
                <ImageIcon className="h-4 w-4" />
                <span>Poster</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="video" className="text-xs">
              <div className="flex flex-col items-center gap-1">
                <Share2 className="h-4 w-4" />
                <span>Video</span>
              </div>
            </TabsTrigger>
          </TabsList>

          {Object.entries(designTypes).map(([key, design]) => (
            <TabsContent key={key} value={key} className="mt-0">
              <div className="space-y-4">
                <div className="text-center p-6 border rounded-md bg-muted/30">
                  <h3 className="font-medium">{design.title}</h3>
                  <p className="text-sm text-muted-foreground">{design.description}</p>
                  
                  <div 
                    id="canva-design-button" 
                    ref={canvaButtonRef}
                    className="mt-4 inline-block"
                  >
                    {/* Canva button will be inserted here */}
                    <Button className="bg-[#00C4CC] hover:bg-[#00ADB5]">
                      Design with Canva
                    </Button>
                  </div>
                </div>
                
                <div className="text-sm space-y-2">
                  <h4 className="font-medium">Tips:</h4>
                  <ul className="list-disc pl-5 text-muted-foreground">
                    <li>Use your brand colors for consistency</li>
                    <li>Include a clear call-to-action</li>
                    <li>Keep text minimal and impactful</li>
                    <li>Use high-quality images</li>
                  </ul>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default CanvaDesignTool;
